import { type ComponentType, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { motion, useReducedMotion } from "motion/react";
import {
  ArrowRight,
  Building2,
  ChevronDown,
  CircleDot,
  FileText,
  PanelRightClose,
  PanelRightOpen,
  Tag,
} from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  KB_COPY,
  kbOnboardingSteps,
  type KbOnboardingStepAccent,
  type KbOnboardingStepIcon,
} from "@/lib/kb-copy";
import { kbInboxPath, kbWorkbenchPath } from "@/lib/kb-routes";
import { cn } from "@/lib/utils";

/** 点击后若指针仍悬停，最长展示时长（毫秒）；指针离开立即关闭 */
const JOURNEY_TOOLTIP_CLICK_MS = 3000;

const STEP_ICONS: Record<
  Exclude<KbOnboardingStepIcon, "use">,
  ComponentType<{ className?: string; "aria-hidden"?: boolean }>
> = {
  source: FileText,
  points: CircleDot,
};

const ACCENT_STYLES: Record<
  KbOnboardingStepAccent,
  { card: string; icon: string; badge: string }
> = {
  amber: {
    card: "border-amber-200/80 bg-amber-50/60 dark:border-amber-900/50 dark:bg-amber-950/30",
    icon: "border-amber-200/90 bg-amber-100/80 text-amber-800 dark:border-amber-800/60 dark:bg-amber-900/40 dark:text-amber-200",
    badge: "bg-amber-600/90 text-white dark:bg-amber-500/90",
  },
  sky: {
    card: "border-sky-200/80 bg-sky-50/60 dark:border-sky-900/50 dark:bg-sky-950/30",
    icon: "border-sky-200/90 bg-sky-100/80 text-sky-800 dark:border-sky-800/60 dark:bg-sky-900/40 dark:text-sky-200",
    badge: "bg-sky-600/90 text-white dark:bg-sky-500/90",
  },
  emerald: {
    card: "border-emerald-200/80 bg-emerald-50/60 dark:border-emerald-900/50 dark:bg-emerald-950/30",
    icon: "border-emerald-200/90 bg-emerald-100/80 text-emerald-800 dark:border-emerald-800/60 dark:bg-emerald-900/40 dark:text-emerald-200",
    badge: "bg-emerald-600/90 text-white dark:bg-emerald-500/90",
  },
};

type KbWorkflowExplainerProps = {
  scope: "enterprise" | "brand";
  accountId: string;
  brandId?: string | null;
};

/**
 * 知识库工作台顶栏：一句话说明 + 可折叠「信息收集 · 知识拆解 · 知识调取」步骤卡。
 * 右侧「知识旅程」为路由开关：/inbox 开，kb 工作台关；再点关闭。
 */
export function KbWorkflowExplainer({
  scope,
  accountId,
  brandId = null,
}: KbWorkflowExplainerProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const steps = kbOnboardingSteps(scope);
  const reduceMotion = useReducedMotion();
  const title = scope === "enterprise" ? "企业知识库" : "品牌知识库";
  const body = scope === "enterprise" ? KB_COPY.onboardingBody : KB_COPY.onboardingBodyBrand;
  const contentScope = { accountId, brandId };
  const journeyOpen = /\/inbox\/?$/.test(pathname);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  /** Tooltip 文案对应的旅程态：点击后立刻用下一态，不等 pathname 回写 */
  const [hintJourneyOpen, setHintJourneyOpen] = useState(journeyOpen);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHideTimer = () => {
    if (hideTimerRef.current != null) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  };

  useEffect(() => () => clearHideTimer(), []);

  useEffect(() => {
    setHintJourneyOpen(journeyOpen);
  }, [journeyOpen]);

  const closeTooltip = () => {
    clearHideTimer();
    setTooltipOpen(false);
  };

  const showTooltipFromClick = () => {
    clearHideTimer();
    setTooltipOpen(true);
    // 指针仍悬停时的短延时 fallback；离开由 onPointerLeave / onBlur 立即关
    hideTimerRef.current = setTimeout(() => {
      setTooltipOpen(false);
      hideTimerRef.current = null;
    }, JOURNEY_TOOLTIP_CLICK_MS);
  };

  const handleTooltipOpenChange = (open: boolean) => {
    if (!open) {
      clearHideTimer();
      setTooltipOpen(false);
      return;
    }
    setTooltipOpen(true);
  };

  const toggleJourney = () => {
    const nextOpen = !journeyOpen;
    setHintJourneyOpen(nextOpen);
    navigate(nextOpen ? kbInboxPath(contentScope) : kbWorkbenchPath(contentScope));
    showTooltipFromClick();
  };

  return (
    <div className="rounded-md border border-border bg-muted/30 px-3 py-2.5 text-sm text-muted-foreground">
      <Collapsible className="group">
        {/* 标题行：左文（标题 / teaser / 了解）+ 右开关「知识旅程」 */}
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            <p className="font-medium text-foreground">{title}</p>
            <span className="text-xs font-normal text-muted-foreground group-data-[state=open]:hidden">
              {KB_COPY.onboardingTeaser}
            </span>
            <CollapsibleTrigger
              className="inline-flex items-center gap-0.5 rounded-md text-xs font-medium text-foreground/70 outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={KB_COPY.onboardingLearnMore}
            >
              <span className="group-data-[state=open]:hidden">了解</span>
              <ChevronDown className="size-3.5 shrink-0 transition-transform group-data-[state=open]:rotate-180" />
            </CollapsibleTrigger>
          </div>
          <Tooltip open={tooltipOpen} onOpenChange={handleTooltipOpenChange}>
            <TooltipTrigger asChild>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className={cn(
                  /* 克制可读：bg-background 抬出 Explainer 的 muted 底，避免 outline 融进底色 */
                  "shrink-0 gap-1.5 border-border bg-background text-foreground hover:bg-muted",
                  journeyOpen &&
                    "border-border bg-accent text-accent-foreground hover:bg-accent/80",
                )}
                aria-pressed={journeyOpen}
                onClick={toggleJourney}
                onPointerLeave={closeTooltip}
                onBlur={closeTooltip}
              >
                {journeyOpen ? (
                  <PanelRightClose className="size-3.5 shrink-0" aria-hidden />
                ) : (
                  <PanelRightOpen className="size-3.5 shrink-0" aria-hidden />
                )}
                {journeyOpen ? KB_COPY.journeyCloseNav : KB_COPY.journeyNav}
                {/* 迷你开关轨道：明示可再点关闭，而非一次性 CTA */}
                <span
                  aria-hidden
                  className={cn(
                    "ml-0.5 inline-flex h-3.5 w-6 shrink-0 items-center rounded-full p-px transition-colors",
                    journeyOpen
                      ? "justify-end bg-primary"
                      : "justify-start bg-muted ring-1 ring-border",
                  )}
                >
                  <span className="size-2.5 rounded-full bg-background shadow-xs" />
                </span>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" sideOffset={8}>
              {hintJourneyOpen ? KB_COPY.journeyCloseHint : KB_COPY.journeyLinkHint}
            </TooltipContent>
          </Tooltip>
        </div>

        <p className="mt-1 leading-relaxed">{body}</p>

        <CollapsibleContent className="mt-3 outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-top-1">
          <p className="mb-2.5 text-[11px] leading-snug text-muted-foreground">
            收集素材 → 拆成知识点（≠ 长文）→ 画像与工具按需调取。
          </p>
          <ol className="flex flex-col gap-2 sm:flex-row sm:items-stretch sm:gap-0">
            {steps.map((step, index) => {
              const accent = ACCENT_STYLES[step.accent];
              const Icon =
                step.icon === "use"
                  ? scope === "enterprise"
                    ? Building2
                    : Tag
                  : STEP_ICONS[step.icon];

              return (
                <li key={step.title} className="flex min-w-0 flex-1 items-stretch sm:items-center">
                  <motion.div
                    className={cn("w-full rounded-md border p-2.5 shadow-xs", accent.card)}
                    initial={reduceMotion ? false : { opacity: 0, y: 8, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={
                      reduceMotion
                        ? { duration: 0 }
                        : { duration: 0.3, delay: index * 0.09, ease: "easeOut" }
                    }
                  >
                    <div className="flex items-start gap-2.5">
                      <div
                        className={cn(
                          "flex size-9 shrink-0 items-center justify-center rounded-md border",
                          accent.icon,
                        )}
                        aria-hidden
                      >
                        <Icon className="size-3.5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
                          <span
                            className={cn(
                              "inline-flex size-4 shrink-0 items-center justify-center rounded-full text-[10px] font-bold tabular-nums",
                              accent.badge,
                            )}
                          >
                            {index + 1}
                          </span>
                          {step.title}
                        </p>
                        <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                  {index < steps.length - 1 ? (
                    <motion.div
                      className="hidden shrink-0 items-center px-1.5 text-muted-foreground/70 sm:flex"
                      aria-hidden
                      initial={reduceMotion ? false : { opacity: 0 }}
                      animate={
                        reduceMotion
                          ? { opacity: 0.7 }
                          : { opacity: [0.35, 1, 0.35], x: [0, 2, 0] }
                      }
                      transition={
                        reduceMotion
                          ? { duration: 0 }
                          : {
                              opacity: {
                                duration: 1.4,
                                delay: 0.35 + index * 0.12,
                                repeat: Infinity,
                                ease: "easeInOut",
                              },
                              x: {
                                duration: 1.4,
                                delay: 0.35 + index * 0.12,
                                repeat: Infinity,
                                ease: "easeInOut",
                              },
                            }
                      }
                    >
                      <ArrowRight className="size-3.5" />
                    </motion.div>
                  ) : null}
                </li>
              );
            })}
          </ol>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
